import React, { useState, useMemo, useEffect } from "react";
import { toast } from "react-toastify";
import Layout from "../../../components/layout/Layout";
import Dialog from "../../../components/ui/Dialog";
import Pagination from "../../../components/ui/Pagination";
import { Link } from "react-router-dom";
import {
  useClients,
  useCreateClient,
  useUpdateClient,
  useToggleClientStatus,
  useGeocodeAddress,
} from "../../../api/queries/organizationQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { useCanModify } from "../../../hooks/usePermissions";
import LocationFields from "../../../components/ui/LocationFields";
import {
  locationPayload,
  locationProblem,
} from "../../../utils/locationFields";

const Client = () => {
  // View-only roles see the list without create / edit / status actions
  const canModify = useCanModify("company-setup", { company: true });
  const { data: clients = [] } = useClients();
  const createClient = useCreateClient();
  const updateClient = useUpdateClient();
  const toggleClientStatus = useToggleClientStatus();
  const geocode = useGeocodeAddress();
  const isSaving = createClient.isPending || updateClient.isPending;

  /* ===================== LOCAL STATE ===================== */
  const [showModal, setShowModal] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingClient, setPendingClient] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const [formData, setFormData] = useState({
    id: "",
    clientCode: "",
    clientName: "",
    details: "",
    contactPerson: "",
    mobile: "",
    email: "",
    address: "",
    website: "",
    latitude: "",
    longitude: "",
    geofenceRadius: "",
    isActive: true,
  });

  /* ===================== FILTER & PAGINATION STATE ===================== */
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [sortConfig, setSortConfig] = useState({
    key: "clientName",
    direction: "asc",
  });

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, statusFilter, sortConfig]);

  /* ===================== HELPER FUNCTIONS ===================== */
  const toCamelCase = (str) => {
    if (!str) return "Active";
    const s = str.toString().toLowerCase();
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  const getSortIcon = (key) => {
    if (sortConfig.key !== key)
      return <i className="bi bi-arrow-down-up ms-2 opacity-25 fs-060"></i>;
    return sortConfig.direction === "asc" ? (
      <i className="bi bi-sort-down ms-2 text-primary fs-070"></i>
    ) : (
      <i className="bi bi-sort-up ms-2 text-primary fs-070"></i>
    );
  };

  const requestSort = (key) => {
    setSortConfig((prev) => ({
      key,
      direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
    }));
  };

  /* ===================== LOGIC HANDLERS ===================== */
  const openAddModal = () => {
    setIsEditMode(false);
    setFormData({
      id: "",
      clientCode: "",
      clientName: "",
      details: "",
      contactPerson: "",
      mobile: "",
      email: "",
      address: "",
      website: "",
      latitude: "",
      longitude: "",
      geofenceRadius: "",
      isActive: true,
    });
    setShowModal(true);
  };

  const openEditModal = (client) => {
    setIsEditMode(true);
    setFormData({
      id: client.id,
      clientCode: client.clientCode || "",
      clientName: client.name || client.clientName || "",
      details: client.details || "",
      contactPerson: client.contactPerson || "",
      mobile: client.mobile || "",
      email: client.email || "",
      address: client.address || "",
      website: client.website || "",
      latitude: client.latitude ?? "",
      longitude: client.longitude ?? "",
      geofenceRadius: client.geofenceRadius ?? "",
      isActive: client.status === "Active" || client.isActive === true,
    });
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    const problem = locationProblem(formData);
    if (problem) {
      toast.error(problem);
      return;
    }

    // Explicit Payload Mapping to ensure 'clientName' is passed to the slice
    const payload = {
      clientName: formData.clientName,
      clientCode: formData.clientCode,
      details: formData.details,
      address: formData.address,
      mobile: formData.mobile,
      email: formData.email,
      contactPerson: formData.contactPerson,
      website: formData.website,
      isActive: formData.isActive,
      ...locationPayload(formData),
    };

    try {
      if (isEditMode) {
        await updateClient.mutateAsync({ id: formData.id, data: payload });
        toast.success(`${formData.clientName} updated!`);
      } else {
        await createClient.mutateAsync(payload);
        toast.success(`${formData.clientName} created!`);
      }
      setShowModal(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to save client"));
    }
  };

  const handleStatusToggle = (client) => {
    const currentStatus =
      client.status || (client.isActive ? "Active" : "Inactive");
    if (currentStatus === "Active") {
      setPendingClient(client);
      setShowConfirm(true);
    } else {
      executeStatusUpdate(client);
    }
  };

  const executeStatusUpdate = async (client) => {
    try {
      const currentStatus =
        client.status || (client.isActive ? "Active" : "Inactive");
      await toggleClientStatus.mutateAsync({ id: client.id, currentStatus });
      toast.success(`${client.name || client.clientName} status updated!`);
      setShowConfirm(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Update failed"));
    }
  };

  /* ===================== FILTER & PAGINATION LOGIC ===================== */
  const processedData = useMemo(() => {
    let items = Array.isArray(clients) ? [...clients] : [];
    items = items.filter((item) => {
      const name = item.name || item.clientName || "";
      const matchesSearch =
        name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (item.clientCode || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase()) ||
        (item.contactPerson || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase());
      const itemStatus = item.status || (item.isActive ? "Active" : "Inactive");
      const matchesStatus =
        statusFilter === "All" || itemStatus === statusFilter;
      return matchesSearch && matchesStatus;
    });

    items.sort((a, b) => {
      const valA = String(a[sortConfig.key] || a.name || "").toLowerCase();
      const valB = String(b[sortConfig.key] || b.name || "").toLowerCase();
      if (valA < valB) return sortConfig.direction === "asc" ? -1 : 1;
      if (valA > valB) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
    return items;
  }, [clients, searchTerm, statusFilter, sortConfig]);

  const totalItems = processedData.length;
  const currentData = processedData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  return (
    <Layout>
      <div className="container-fluid py-4 px-lg-4">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-4">
          <div>
            <Link
              to="/admin"
              className="text-slate-500 fs-080 text-decoration-none"
            >
              <i className="bi bi-arrow-left"></i> Administration
            </Link>
            <h1 className="fw-bold text-dark m-0 page-title">Payroll Group</h1>
            <p className="text-muted small mb-0">
              Manage customer accounts and workforce distribution
            </p>
          </div>
          {canModify && (
            <button
              className="btn btn-sm px-3 text-white btn-navy h-35px d-flex align-items-center"
              onClick={openAddModal}
            >
              <i className="bi bi-plus-lg me-2"></i>Add New
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="row g-2 mb-4">
          <div className="col-md-9">
            <input
              type="text"
              className="form-control filter-input"
              placeholder="Search by name, code, or contact person..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="col-md-3">
            <select
              className="form-select filter-input"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="All">All Status</option>
              <option value="Active">Active</option>
              <option value="Inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Glass Table Container */}

        <div className="glass-table-card">
          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="custom-table-header sortable">
                <tr>
                  <th
                    className="ps-4 py-3 border-0"
                    onClick={() => requestSort("clientCode")}
                  >
                    CODE {getSortIcon("clientCode")}
                  </th>
                  <th
                    className="py-3 border-0"
                    onClick={() => requestSort("clientName")}
                  >
                    NAME {getSortIcon("clientName")}
                  </th>
                  <th className="py-3 border-0">CONTACT</th>
                  <th className="py-3 border-0 text-center">STATUS</th>
                  <th className="pe-4 py-3 border-0 text-end">ACTIONS</th>
                </tr>
              </thead>
              <tbody className="border-top-0">
                {currentData.map((item) => (
                  <tr key={item.id} className="align-middle">
                    <td className="ps-4 py-3 fw-bold text-primary fs-085">
                      {item.clientCode}
                    </td>
                    <td className="py-3">
                      <div className="fw-bold text-dark fs-090">
                        {item.name || item.clientName}
                      </div>
                      <div className="text-muted fs-075">{item.email}</div>
                    </td>
                    <td className="py-3">
                      <div className="fs-085">{item.contactPerson}</div>
                      <div className="text-muted fs-075">{item.mobile}</div>
                    </td>
                    <td className="text-center">
                      <span
                        className={`status-pill status-pill--${item.status === "Active" || item.isActive ? "green" : "gray"}`}
                      >
                        {toCamelCase(
                          item.status ||
                            (item.isActive ? "Active" : "Inactive"),
                        )}
                      </span>
                    </td>
                    <td className="pe-4 text-end">
                      {canModify ? (
                        <>
                          <div className="d-flex justify-content-end gap-2">
                            <button
                              className="btn btn-sm btn-light border"
                              onClick={() => openEditModal(item)}
                            >
                              Edit
                            </button>
                            <button
                              className={`btn btn-sm ${item.status === "Active" || item.isActive ? "btn-outline-danger" : "btn-outline-success"}`}
                              onClick={() => handleStatusToggle(item)}
                            >
                              {item.status === "Active" || item.isActive
                                ? "Deactivate"
                                : "Activate"}
                            </button>
                          </div>
                        </>
                      ) : (
                        <span className="fs-070 text-slate-400">View only</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <Pagination
            page={currentPage}
            limit={itemsPerPage}
            total={totalItems}
            onPageChange={setCurrentPage}
            onLimitChange={(v) => {
              setItemsPerPage(v);
              setCurrentPage(1);
            }}
            pageSizes={[10, 25, 50]}
          />
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirm && (
        <Dialog
          title="Confirm Deactivation"
          onClose={() => setShowConfirm(false)}
          size="sm"
        >
          <div className="text-start">
            <p className="text-muted small">
              Are you sure you want to deactivate{" "}
              <strong>
                {pendingClient?.name || pendingClient?.clientName}
              </strong>
              ?
            </p>
            <div className="d-flex justify-content-end gap-2 mt-3">
              <button
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowConfirm(false)}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger profile-btn"
                onClick={() => executeStatusUpdate(pendingClient)}
              >
                Yes, Deactivate
              </button>
            </div>
          </div>
        </Dialog>
      )}

      {/* Main Modal */}
      {showModal && (
        <Dialog
          title={<>{isEditMode ? "Edit" : "Add"} Client</>}
          onClose={() => setShowModal(false)}
          size="lg"
        >
          <form onSubmit={handleSave}>
            <div className="row g-3">
              <div className="col-md-5">
                <label className="profile-label">Payroll Group Code</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={formData.clientCode}
                  onChange={(e) =>
                    setFormData({ ...formData, clientCode: e.target.value })
                  }
                />
              </div>
              <div className="col-md-7">
                <label className="profile-label">Payroll Group</label>
                <input
                  type="text"
                  className="form-control"
                  required
                  value={formData.clientName}
                  onChange={(e) =>
                    setFormData({ ...formData, clientName: e.target.value })
                  }
                />
              </div>
              <div className="col-12">
                <label className="profile-label">Details/Description</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={formData.details}
                  onChange={(e) =>
                    setFormData({ ...formData, details: e.target.value })
                  }
                ></textarea>
              </div>
              <div className="col-md-6">
                <label className="profile-label">Contact Person</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.contactPerson}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      contactPerson: e.target.value,
                    })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="profile-label">Mobile</label>
                <input
                  type="text"
                  className="form-control"
                  value={formData.mobile}
                  onChange={(e) =>
                    setFormData({ ...formData, mobile: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="profile-label">Email</label>
                <input
                  type="email"
                  className="form-control"
                  value={formData.email}
                  onChange={(e) =>
                    setFormData({ ...formData, email: e.target.value })
                  }
                />
              </div>
              <div className="col-md-6">
                <label className="profile-label">Website</label>
                <input
                  type="text"
                  className="form-control"
                  placeholder="https://www.acme.com"
                  value={formData.website}
                  onChange={(e) =>
                    setFormData({ ...formData, website: e.target.value })
                  }
                />
              </div>
              <div className="col-12">
                <label className="profile-label">Address</label>
                <textarea
                  className="form-control"
                  rows="2"
                  value={formData.address}
                  onChange={(e) =>
                    setFormData({ ...formData, address: e.target.value })
                  }
                ></textarea>
              </div>
              <LocationFields
                geocode={geocode}
                hint="Used for client visit check-ins. Without a location, check-ins at this client are flagged for review."
                address={formData.address}
                values={formData}
                onChange={(changes) =>
                  setFormData((prev) => ({ ...prev, ...changes }))
                }
              />
              <div className="col-12">
                <div className="form-check form-switch mt-2">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={(e) =>
                      setFormData({ ...formData, isActive: e.target.checked })
                    }
                  />
                  <label className="form-check-label fs-085">
                    Active Status
                  </label>
                </div>
              </div>
            </div>
            <div className="d-flex justify-content-end gap-2 mt-3 pt-3 border-top">
              <button
                type="button"
                className="btn btn-outline-secondary profile-btn"
                onClick={() => setShowModal(false)}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn btn-primary profile-btn"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Client"}
              </button>
            </div>
          </form>
        </Dialog>
      )}
    </Layout>
  );
};

export default Client;
